/**
 * LiveKit data-channel sync for Q&A / announcements / polls / chat.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useMaybeRoomContext } from "@livekit/components-react";
import { RoomEvent } from "livekit-client";
import type { LiveSessionNotice } from "./LiveSessionNoticeToast";
import {
  LIVE_ANNOUNCEMENT_TYPE,
  LIVE_ANNOUNCEMENT_RETRACT_TYPE,
  LIVE_CHAT_ACCESS_TYPE,
  LIVE_CHAT_ENGAGE_TYPE,
  LIVE_CHAT_RETRACT_TYPE,
  LIVE_CHAT_TYPE,
  LIVE_POLL_TYPE,
  LIVE_QA_RETRACT_TYPE,
  LIVE_QA_TYPE,
  type ChatAccess,
  type ChatMessage,
  type ChatReaction,
  type LiveAnnouncement,
  type LiveAnnouncementMessage,
  type LiveChatAccessMessage,
  type LiveChatEngageMessage,
  type LiveChatMessage,
  type LivePoll,
  type LivePollMessage,
  type LiveQaMessage,
  type QaItem,
} from "./liveCollaborationTypes";
import {
  filterQaDestinationsInRoom,
  qaPublishDestinations,
  qaTrainerDestinations,
  chatDmDestinations,
  trainerIdentitiesInRoom,
} from "./qaLivekitPublish";
import {
  chatNoticePeerId,
  chatNoticeText,
  rememberChatId,
  shouldNotifyIncomingChat,
} from "./chatNotify";

function encode(msg: object): Uint8Array {
  return new TextEncoder().encode(JSON.stringify(msg));
}

function decode(data: Uint8Array): Record<string, unknown> | null {
  try {
    return JSON.parse(new TextDecoder().decode(data)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function qaPreview(item: QaItem): string {
  const from = item.is_anonymous
    ? "Anonymous"
    : item.asked_by?.display_name || "Someone";
  const body = String(item.question || "").trim();
  const short = body.length > 80 ? `${body.slice(0, 77)}…` : body;
  return short ? `New Q&A from ${from}: ${short}` : `New Q&A from ${from}`;
}

function upsertChatMessage(
  prev: ChatMessage[],
  message: ChatMessage,
): ChatMessage[] {
  const idx = prev.findIndex((m) => m.message_id === message.message_id);
  if (idx < 0) return [...prev, message];
  const existing = prev[idx];
  // Counts come from the payload; keep this viewer's like/react flags.
  const mergedReactions = (message.reactions || []).map((r) => {
    const prevR = (existing.reactions || []).find((x) => x.emoji === r.emoji);
    return {
      emoji: r.emoji,
      count: r.count,
      i_reacted: Boolean(r.i_reacted) || Boolean(prevR?.i_reacted),
    };
  });
  const next = [...prev];
  next[idx] = {
    ...existing,
    ...message,
    i_liked: Boolean(existing.i_liked),
    reactions: mergedReactions,
  };
  return next;
}

function applyChatEngage(
  prev: ChatMessage[],
  payload: {
    message_id: string;
    like_count: number;
    reactions: ChatReaction[];
  },
): ChatMessage[] {
  const idx = prev.findIndex((m) => m.message_id === payload.message_id);
  if (idx < 0) return prev;
  const existing = prev[idx];
  const mergedReactions = (payload.reactions || []).map((r) => {
    const prevR = (existing.reactions || []).find((x) => x.emoji === r.emoji);
    return {
      emoji: r.emoji,
      count: r.count,
      // Payload is the full set after override — keep my flag only for emojis still present.
      i_reacted: Boolean(r.i_reacted) || Boolean(prevR?.i_reacted),
    };
  });
  const next = [...prev];
  next[idx] = {
    ...existing,
    like_count: payload.like_count,
    reactions: mergedReactions,
  };
  return next;
}

export function useLiveCollaborationSync(options: {
  enabled?: boolean;
  /** Skip toasts for own chat / announcements / Q&A. */
  localIdentity?: string;
  /** Assigned trainer id — required for private student Q&A fan-out. */
  trainerIdentity?: string;
}) {
  const room = useMaybeRoomContext();
  const [qaItems, setQaItemsState] = useState<QaItem[]>([]);
  const [announcements, setAnnouncements] = useState<LiveAnnouncement[]>([]);
  const [activePoll, setActivePoll] = useState<LivePoll | null>(null);
  const [chatMessages, setChatMessagesState] = useState<ChatMessage[]>([]);
  const [chatAccess, setChatAccess] = useState<ChatAccess | null>(null);
  const [notices, setNotices] = useState<LiveSessionNotice[]>([]);
  /** Q&A ids already toasted or loaded via bootstrap (avoid spam / re-toast). */
  const knownQaIdsRef = useRef<Set<string>>(new Set());
  /** Chat ids already toasted or loaded via bootstrap (avoid spam / re-toast). */
  const knownChatIdsRef = useRef<Set<string>>(new Set());
  const knownAnnouncementIdsRef = useRef<Set<string>>(new Set());

  const dismissNotice = useCallback((id?: string) => {
    if (!id) {
      setNotices([]);
      return;
    }
    setNotices((prev) => prev.filter((n) => n.id !== id));
  }, []);

  /** Keep last 3 toasts; replace same id if it arrives again. */
  const pushNotice = useCallback(
    (
      text: string,
      id: string,
      extra?: Pick<LiveSessionNotice, "action" | "durationMs">,
    ) => {
      setNotices((prev) => {
        const next = [
          ...prev.filter((n) => n.id !== id),
          { id, text, ...extra },
        ];
        return next.slice(-3);
      });
    },
    [],
  );

  const setQaItems = useCallback(
    (update: QaItem[] | ((prev: QaItem[]) => QaItem[])) => {
      setQaItemsState((prev) => {
        const next = typeof update === "function" ? update(prev) : update;
        // Bulk replace (bootstrap / REST hydrate) — seed ids, no toasts
        if (typeof update !== "function") {
          for (const q of next) {
            const id = String(q.qa_id || "").trim();
            if (id) knownQaIdsRef.current.add(id);
          }
        }
        return next;
      });
    },
    [],
  );

  const setChatMessages = useCallback(
    (update: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => {
      setChatMessagesState((prev) => {
        const next = typeof update === "function" ? update(prev) : update;
        // Bulk replace (bootstrap / REST hydrate) — seed ids, no toasts
        if (typeof update !== "function") {
          for (const m of next) {
            rememberChatId(
              knownChatIdsRef.current,
              String(m.message_id || "").trim(),
            );
          }
        }
        return next;
      });
    },
    [],
  );

  const seedAnnouncements = useCallback((items: LiveAnnouncement[]) => {
    for (const a of items) {
      const id = String(a.announcement_id || "").trim();
      if (id) knownAnnouncementIdsRef.current.add(id);
    }
  }, []);

  const setAnnouncementsBulk = useCallback(
    (update: LiveAnnouncement[] | ((prev: LiveAnnouncement[]) => LiveAnnouncement[])) => {
      setAnnouncements((prev) => {
        const next = typeof update === "function" ? update(prev) : update;
        if (typeof update !== "function") seedAnnouncements(next);
        return next;
      });
    },
    [seedAnnouncements],
  );

  const notifyIncomingQa = useCallback(
    (item: QaItem) => {
      const localId = String(options.localIdentity || "").trim();
      const qaId = String(item.qa_id || "").trim();
      const status = String(item.status || "open");
      const askerId = String(item.asked_by?.identity || "").trim();
      const isNew = Boolean(qaId) && !knownQaIdsRef.current.has(qaId);
      if (qaId) knownQaIdsRef.current.add(qaId);
      if (
        isNew &&
        status === "open" &&
        (!localId || !askerId || askerId !== localId)
      ) {
        pushNotice(qaPreview(item), `qa-${qaId}`, {
          action: { type: "open_qa", qaId },
        });
      }
    },
    [options.localIdentity, pushNotice],
  );

  const notifyIncomingAnnouncement = useCallback(
    (item: LiveAnnouncement) => {
      const localId = String(options.localIdentity || "").trim();
      const announcementId = String(item.announcement_id || "").trim();
      const isNewAnn =
        Boolean(announcementId) &&
        !knownAnnouncementIdsRef.current.has(announcementId);
      if (announcementId) knownAnnouncementIdsRef.current.add(announcementId);
      const authorId = String(item.author?.identity || "").trim();
      if (isNewAnn && (!localId || authorId !== localId)) {
        const title = String(item.title || "").trim() || "Announcement";
        pushNotice(`New announcement: ${title}`, `ann-${announcementId}`, {
          action: {
            type: "open_announcements",
            announcementId: announcementId || undefined,
          },
        });
      }
    },
    [options.localIdentity, pushNotice],
  );

  const notifyIncomingChat = useCallback(
    (message: ChatMessage) => {
      const localId = String(options.localIdentity || "").trim();
      const decision = shouldNotifyIncomingChat({
        localIdentity: localId,
        message,
        knownIds: knownChatIdsRef.current,
      });
      if (decision.messageId) {
        rememberChatId(knownChatIdsRef.current, decision.messageId);
      }
      if (!decision.notify) return;
      const isPrivate = message.channel === "dm";
      pushNotice(chatNoticeText(message), `chat-${decision.messageId}`, {
        action: {
          type: "open_chat",
          messageId: decision.messageId || undefined,
          peerId: chatNoticePeerId(message),
        },
        durationMs: isPrivate ? 6000 : undefined,
      });
    },
    [options.localIdentity, pushNotice],
  );

  useEffect(() => {
    if (!options.enabled || !room) return;
    const localId = String(options.localIdentity || "").trim();

    const onData = (payload: Uint8Array) => {
      const raw = decode(payload);
      if (!raw || Number(raw.v) !== 1) return;
      if (raw.type === LIVE_QA_TYPE && raw.item) {
        const item = raw.item as QaItem;
        const qaId = String(item.qa_id || "").trim();
        const status = String(item.status || "open");
        const askerId = String(item.asked_by?.identity || "").trim();
        const isNew = Boolean(qaId) && !knownQaIdsRef.current.has(qaId);
        if (qaId) knownQaIdsRef.current.add(qaId);

        setQaItemsState((prev) => {
          const merged: QaItem = {
            ...item,
            status: (item.status as QaItem["status"]) || "open",
          };
          const rest = prev.filter((q) => q.qa_id !== item.qa_id);
          return [merged, ...rest];
        });

        // Toast outside setState (same pattern as chat) so it always fires
        if (
          isNew &&
          status === "open" &&
          (!localId || !askerId || askerId !== localId)
        ) {
          pushNotice(qaPreview(item), `qa-${qaId}`, {
            action: { type: "open_qa", qaId },
          });
        }
      } else if (raw.type === LIVE_ANNOUNCEMENT_TYPE && raw.item) {
        const item = raw.item as LiveAnnouncement;
        const announcementId = String(item.announcement_id || "").trim();
        const isNewAnn =
          Boolean(announcementId) &&
          !knownAnnouncementIdsRef.current.has(announcementId);
        if (announcementId) knownAnnouncementIdsRef.current.add(announcementId);
        setAnnouncements((prev) => {
          const rest = prev.filter(
            (a) => a.announcement_id !== item.announcement_id,
          );
          const next = [item, ...rest];
          next.sort((a, b) => Number(b.pinned) - Number(a.pinned));
          return next;
        });
        const authorId = String(item.author?.identity || "").trim();
        if (
          isNewAnn &&
          (!localId || authorId !== localId)
        ) {
          const title = String(item.title || "").trim() || "Announcement";
          pushNotice(`New announcement: ${title}`, `ann-${announcementId}`, {
            action: {
              type: "open_announcements",
              announcementId: announcementId || undefined,
            },
          });
        }
      } else if (raw.type === LIVE_POLL_TYPE && raw.poll) {
        const incoming = raw.poll as LivePoll;
        setActivePoll(incoming.status === "ended" ? null : incoming);
      } else if (raw.type === LIVE_CHAT_TYPE && raw.message) {
        const message = raw.message as ChatMessage;
        const decision = shouldNotifyIncomingChat({
          localIdentity: localId,
          message,
          knownIds: knownChatIdsRef.current,
        });
        if (decision.messageId) {
          rememberChatId(knownChatIdsRef.current, decision.messageId);
        }
        setChatMessagesState((prev) => upsertChatMessage(prev, message));
        if (decision.notify) {
          const isPrivate = message.channel === "dm";
          pushNotice(chatNoticeText(message), `chat-${decision.messageId}`, {
            action: {
              type: "open_chat",
              messageId: decision.messageId || undefined,
              peerId: chatNoticePeerId(message),
            },
            durationMs: isPrivate ? 6000 : undefined,
          });
        }
      } else if (
        raw.type === LIVE_CHAT_RETRACT_TYPE &&
        raw.message_id
      ) {
        const mid = String(raw.message_id || "").trim();
        if (mid) {
          setChatMessagesState((prev) => prev.filter((m) => m.message_id !== mid));
        }
      } else if (raw.type === LIVE_QA_RETRACT_TYPE && raw.qa_id) {
        const qid = String(raw.qa_id || "").trim();
        if (qid) {
          knownQaIdsRef.current.delete(qid);
          setQaItemsState((prev) => prev.filter((q) => q.qa_id !== qid));
        }
      } else if (
        raw.type === LIVE_ANNOUNCEMENT_RETRACT_TYPE &&
        raw.announcement_id
      ) {
        const aid = String(raw.announcement_id || "").trim();
        if (aid) {
          setAnnouncements((prev) =>
            prev.filter((a) => a.announcement_id !== aid),
          );
        }
      } else if (raw.type === LIVE_CHAT_ENGAGE_TYPE && raw.message_id) {
        setChatMessagesState((prev) =>
          applyChatEngage(prev, {
            message_id: String(raw.message_id),
            like_count: Math.max(0, Number(raw.like_count || 0)),
            reactions: Array.isArray(raw.reactions)
              ? (raw.reactions as ChatReaction[])
              : [],
          }),
        );
      } else if (raw.type === LIVE_CHAT_ACCESS_TYPE && raw.access) {
        setChatAccess(raw.access as ChatAccess);
      }
    };

    room.on(RoomEvent.DataReceived, onData);
    return () => {
      room.off(RoomEvent.DataReceived, onData);
    };
  }, [options.enabled, options.localIdentity, pushNotice, room]);

  const publishQa = async (
    item: QaItem,
    publishOpts?: { broadcast?: boolean; roomWide?: boolean },
  ) => {
    const qaId = String(item.qa_id || "").trim();
    if (qaId) knownQaIdsRef.current.add(qaId);
    setQaItemsState((prev) => {
      const rest = prev.filter((q) => q.qa_id !== item.qa_id);
      return [item, ...rest];
    });
    if (publishOpts?.broadcast === false) return;
    if (!room?.localParticipant) {
      console.warn("Q&A LiveKit fan-out skipped: not connected to room");
      return;
    }
    const msg: LiveQaMessage = { type: LIVE_QA_TYPE, v: 1, item };
    // Private answers → asker + trainer only (not all students in the room).
    // Pin / show / hide use roomWide so every student updates without refresh.
    const opts: { reliable: true; destinationIdentities?: string[] } = {
      reliable: true,
    };
    const dest = filterQaDestinationsInRoom(
      room,
      qaPublishDestinations({
        item,
        publisherIdentity: room.localParticipant.identity,
        trainerIdentity: options.trainerIdentity,
        trainerIdentities: qaTrainerDestinations(room, options.trainerIdentity),
        roomWide: Boolean(publishOpts?.roomWide),
      }),
    );
    if (dest?.length) opts.destinationIdentities = dest;
    try {
      await room.localParticipant.publishData(encode(msg), opts);
    } catch (err) {
      console.warn("Q&A LiveKit fan-out failed", err);
    }
  };

  const publishAnnouncement = async (
    item: LiveAnnouncement,
    options?: { broadcast?: boolean },
  ) => {
    setAnnouncements((prev) => {
      const rest = prev.filter(
        (a) => a.announcement_id !== item.announcement_id,
      );
      return [item, ...rest];
    });
    if (options?.broadcast === false) return;
    if (!room?.localParticipant) return;
    const msg: LiveAnnouncementMessage = {
      type: LIVE_ANNOUNCEMENT_TYPE,
      v: 1,
      item,
    };
    await room.localParticipant.publishData(encode(msg), { reliable: true });
  };

  const publishPoll = async (
    poll: LivePoll,
    options?: { broadcast?: boolean },
  ) => {
    setActivePoll(poll.status === "ended" ? null : poll);
    if (options?.broadcast === false) return;
    if (!room?.localParticipant) return;
    const msg: LivePollMessage = { type: LIVE_POLL_TYPE, v: 1, poll };
    await room.localParticipant.publishData(encode(msg), { reliable: true });
  };

  const publishChat = async (
    message: ChatMessage,
    options?: { broadcast?: boolean },
  ) => {
    rememberChatId(
      knownChatIdsRef.current,
      String(message.message_id || "").trim(),
    );
    setChatMessagesState((prev) => upsertChatMessage(prev, message));
    if (options?.broadcast === false) return;
    if (!room?.localParticipant) return;
    const msg: LiveChatMessage = { type: LIVE_CHAT_TYPE, v: 1, message };
    const opts: { reliable: true; destinationIdentities?: string[] } = {
      reliable: true,
    };
    if (message.channel === "dm") {
      const trainers = trainerIdentitiesInRoom(room);
      const dest = filterQaDestinationsInRoom(
        room,
        chatDmDestinations({
          publisherIdentity: room.localParticipant.identity,
          senderIdentity: message.sender?.identity,
          recipientIdentity: message.recipient?.identity,
          trainerIdentities: trainers,
        }),
      );
      if (dest?.length) opts.destinationIdentities = dest;
    }
    await room.localParticipant.publishData(encode(msg), opts);
  };

  /** Scale: tiny like/reaction delta instead of full chat body. */
  const publishChatEngage = async (message: ChatMessage) => {
    if (!room?.localParticipant) return;
    const mid = String(message.message_id || "").trim();
    if (!mid) return;
    setChatMessagesState((prev) => upsertChatMessage(prev, message));
    const payload: LiveChatEngageMessage = {
      type: LIVE_CHAT_ENGAGE_TYPE,
      v: 1,
      message_id: mid,
      like_count: Math.max(0, Number(message.like_count || 0)),
      reactions: (message.reactions || []).map((r) => ({
        emoji: r.emoji,
        count: r.count,
        i_reacted: false,
      })),
      at: Date.now(),
    };
    const opts: { reliable: true; destinationIdentities?: string[] } = {
      reliable: true,
    };
    if (message.channel === "dm") {
      const trainers = trainerIdentitiesInRoom(room);
      const dest = filterQaDestinationsInRoom(
        room,
        chatDmDestinations({
          publisherIdentity: room.localParticipant.identity,
          senderIdentity: message.sender?.identity,
          recipientIdentity: message.recipient?.identity,
          trainerIdentities: trainers,
        }),
      );
      if (dest?.length) opts.destinationIdentities = dest;
    }
    await room.localParticipant.publishData(encode(payload), opts);
  };

  const publishChatAccess = async (access: ChatAccess) => {
    if (!room?.localParticipant) return;
    const msg: LiveChatAccessMessage = {
      type: LIVE_CHAT_ACCESS_TYPE,
      v: 1,
      access,
    };
    setChatAccess(access);
    await room.localParticipant.publishData(encode(msg), { reliable: true });
  };

  const removeChatMessage = (
    messageId: string,
    options?: { broadcast?: boolean },
  ) => {
    const mid = String(messageId || "").trim();
    if (!mid) return;
    setChatMessagesState((prev) => prev.filter((m) => m.message_id !== mid));
    if (options?.broadcast === false) return;
    if (!room?.localParticipant) return;
    void room.localParticipant.publishData(
      encode({ type: LIVE_CHAT_RETRACT_TYPE, v: 1, message_id: mid }),
      { reliable: true },
    );
  };

  const removeAnnouncement = (
    announcementId: string,
    options?: { broadcast?: boolean },
  ) => {
    const aid = String(announcementId || "").trim();
    if (!aid) return;
    setAnnouncements((prev) => prev.filter((a) => a.announcement_id !== aid));
    if (options?.broadcast === false) return;
    if (!room?.localParticipant) return;
    void room.localParticipant.publishData(
      encode({
        type: LIVE_ANNOUNCEMENT_RETRACT_TYPE,
        v: 1,
        announcement_id: aid,
      }),
      { reliable: true },
    );
  };

  return {
    qaItems,
    setQaItems,
    announcements,
    setAnnouncements: setAnnouncementsBulk,
    activePoll,
    setActivePoll,
    chatMessages,
    setChatMessages,
    chatAccess,
    setChatAccess,
    notices,
    /** Latest notice (compat). */
    notice: notices.length ? notices[notices.length - 1] : null,
    dismissNotice,
    notifyIncomingQa,
    notifyIncomingAnnouncement,
    notifyIncomingChat,
    publishQa,
    publishAnnouncement,
    publishPoll,
    publishChat,
    publishChatEngage,
    publishChatAccess,
    removeChatMessage,
    removeAnnouncement,
  };
}
